import { Injectable } from '@angular/core';
import { LogLevel, OidcConfigService, OidcSecurityService, TokenHelperService } from 'angular-auth-oidc-client';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { AppSettingsService } from '../../shared/services/app-settings/app-settings.service';
import { ConfigurationService } from '../../shared/services/configuration/configuration.service';
import { SettingsClient } from '../../api/wallet-api.generated';
import {DISMISSED_BANNER_KEY, LOGOUT_MARKER, REDIRECT} from '../../shared/constants/value';
import {LocalStorageService} from '../../shared/services/storage/local-storage/local-storage.service';

export const configureAuth = (
  oidcConfigService: OidcConfigService,
  settingsClient: SettingsClient,
  configurationService: ConfigurationService
) => {
  const setupAction$ = AppSettingsService.loadSettings$(settingsClient, configurationService).pipe(
    map((cfg) => ({
      stsServer: cfg.authIssuerUrl,
      redirectUrl: window.location.origin,
      clientId: 'portal',
      responseType: 'code',
      scope: 'openid profile portal wallet auth offline_access',
      postLogoutRedirectUri: window.location.origin,
      startCheckSession: false, // deactived due to issues with logout in incognito windows & after pw reset
      silentRenew: true,
      // silentRenewUrl: window.location.origin + '/silent-renew.html',
      postLoginRoute: '/',
      forbiddenRoute: '/forbidden',
      unauthorizedRoute: '/unauthorized',
      logLevel: LogLevel.None,
      maxIdTokenIatOffsetAllowedInSeconds: 30,
      historyCleanupOff: true,
      useRefreshToken: true,
      renewTimeBeforeTokenExpiresInSeconds: 10,

      // autoUserinfo: false,
    })),
    switchMap((config) => oidcConfigService.withConfig(config))
  );

  return () => setupAction$.toPromise();
};

export enum AuthState {
  notAuthenticated = 0,
  incompleteRegistration,
  authenticated,
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  userData$: Observable<any>;
  authState$: Observable<AuthState>;
  checkSessionChanged$: Observable<boolean>;

  constructor(private oidcSecurityService: OidcSecurityService,
              private tokenHelper: TokenHelperService,
              private localStorageService: LocalStorageService) {
    this.userData$ = oidcSecurityService.userData$; //.pipe(tap((val) => console.log(val)));
    this.authState$ = oidcSecurityService.isAuthenticated$.pipe(map((val) => this.mapToAuthState(val)));
    this.checkSessionChanged$ = oidcSecurityService.checkSessionChanged$.pipe();
  }

  isLoggedIn(): Observable<boolean> {
    return this.oidcSecurityService.isAuthenticated$;
  }

  getToken(): string {
    return this.oidcSecurityService.getToken();
  }

  getTokenPayload(): any {
    return this.tokenHelper.getPayloadFromToken(this.oidcSecurityService.getToken(), false);
  }

  userData(): Observable<any> {
    return this.oidcSecurityService.userData$;
  }

  checkAuth(): Observable<AuthState> {
    return this.oidcSecurityService.checkAuth().pipe(map((val) => this.mapToAuthState(val)));
  }

  logoff() {
    this.localStorageService.write(LOGOUT_MARKER, true);
    this.localStorageService.remove(REDIRECT);
    this.localStorageService.remove(DISMISSED_BANNER_KEY);
    this.oidcSecurityService.logoff();
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  authorize(authOptions: { customParams: { ui_locales: string } }) {
    this.oidcSecurityService.authorize(authOptions);
  }

  refreshSession(): Observable<{ idToken: string; accessToken: string }> {
    return this.oidcSecurityService.forceRefreshSession();
  }

  logoffLocal() {
    this.oidcSecurityService.logoffLocal();
  }

  private mapToAuthState(isAuthenticated: boolean) {
    if (isAuthenticated) {
      const token = this.tokenHelper.getPayloadFromToken(this.oidcSecurityService.getToken(), false);
      return !token.incomplete ? AuthState.authenticated : AuthState.incompleteRegistration;
    } else {
      return AuthState.notAuthenticated;
    }
  }
}
